import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, RefreshCw, Eye, Rocket, Copy, Archive, Trash2, Undo2, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useJixxeoBridge } from "@/pages/GeneratedAppHost";

const btn = "mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1 disabled:opacity-40";
const err = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
const inp = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm";
const CATS = ["games", "social", "productivity", "entertainment", "utilities", "education", "lifestyle", "music", "health"];

function NewApp({ onCreated }) {
  const [f, setF] = useState({ name: "", prompt: "", description: "", category: "utilities", design: "", colors: "black and cyan", features: "", monetization: "Free", devices: "Mobile, desktop, installed PWA" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm("Start an AI build? This uses AI credits (Universal Key) — typically a few cents per build.")) return;
    setBusy(true);
    try { const r = await api.post("/admin/appgen/apps", f); toast.success("Build started"); onCreated(r.data.id); } catch (er) { err(er); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="glass rounded-2xl p-4 space-y-2" data-testid="appgen-new-form">
      <div className="font-heading font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#00F0FF]" /> Describe your app</div>
      <textarea required minLength={10} rows={4} value={f.prompt} onChange={set("prompt")} placeholder="e.g. A neighborhood book-swap app where members list books, browse others' shelves and request swaps…" data-testid="appgen-prompt" className={inp} />
      <div className="grid sm:grid-cols-2 gap-2">
        <input required minLength={2} value={f.name} onChange={set("name")} placeholder="App name" data-testid="appgen-name" className={inp} />
        <select value={f.category} onChange={set("category")} data-testid="appgen-category" className={inp}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
        <input value={f.description} onChange={set("description")} placeholder="Short store description" className={inp} />
        <input value={f.design} onChange={set("design")} placeholder="Design style (e.g. playful, neon, minimal)" className={inp} />
        <input value={f.colors} onChange={set("colors")} placeholder="Colors" className={inp} />
        <input value={f.monetization} onChange={set("monetization")} placeholder="Monetization" className={inp} />
      </div>
      <textarea rows={2} value={f.features} onChange={set("features")} placeholder="Must-have features (one per line)" className={inp} />
      <input value={f.devices} onChange={set("devices")} placeholder="Target devices" className={inp} />
      <button disabled={busy} data-testid="appgen-build-btn" className={`${btn} border-[#00F0FF] text-[#00F0FF]`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Generate app</button>
    </form>
  );
}

const STAGES = { code: [5, 70, 150, "Writing app code"], repair: [72, 85, 120, "Repairing issues"], validate: [70, 72, 5, "Checking code"], art: [85, 98, 60, "Creating icon & cover"] };

function BuildProgress({ v }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const st = STAGES[v.stage] || STAGES.code;
  const started = new Date(v.stage_started || v.created_at).getTime();
  const secs = Math.max(0, (now - started) / 1000);
  const frac = 1 - Math.exp(-secs / st[2]);
  const pct = Math.round(st[0] + (st[1] - st[0]) * frac);
  const remainingStage = Math.max(5, st[2] - secs);
  const after = { code: 15 + (v.note === "Initial build" ? 60 : 0), validate: 60, repair: 60, art: 0 }[v.stage] ?? 0;
  const left = Math.round(remainingStage + after);
  return (
    <div className="space-y-1" data-testid="appgen-progress">
      <div className="flex justify-between mono text-[10px] uppercase tracking-widest"><span className="text-[#00F0FF]">{st[3]}…</span><span className="text-white/60" data-testid="appgen-progress-pct">{pct}% · ~{left >= 60 ? `${Math.ceil(left / 60)} min` : `${left}s`} left</span></div>
      <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#00F0FF] to-[#39FF14] transition-[width] duration-1000" style={{ width: `${pct}%` }} /></div>
      <div className="mono text-[10px] text-white/40">Elapsed {Math.floor((now - new Date(v.created_at).getTime()) / 1000)}s. You can leave this page — the build keeps running.</div>
    </div>
  );
}

function Preview({ app, version, onLog }) {
  const ref = useRef(null);
  useJixxeoBridge(ref, app.slug, onLog);
  return (
    <iframe ref={ref} key={`${app.slug}-${version}`} title="preview" data-testid="appgen-preview-frame"
      src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/appgen/preview/${app.slug}?v=${version}`}
      sandbox="allow-scripts allow-forms allow-popups allow-modals" className="w-full h-[560px] rounded-xl border border-white/10 bg-white" />
  );
}

function Detail({ id, onChanged }) {
  const [d, setD] = useState(null);
  const [change, setChange] = useState("");
  const [showPrev, setShowPrev] = useState(false);
  const [runtime, setRuntime] = useState([]);
  const load = useCallback(() => api.get(`/admin/appgen/apps/${id}`).then((r) => setD(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.app.status !== "building") return;
    const t = setInterval(load, 3000); return () => clearInterval(t);
  }, [d?.app.status, load]);
  const onLog = useCallback((l) => setRuntime((r) => [...r.slice(-20), l.msg]), []);
  if (!d) return <div className="mono text-xs animate-pulse">Loading…</div>;
  const { app, versions } = d;
  const cur = versions.find((v) => v.version === app.current_version);
  const latest = versions[0];
  const act = async (action, extra = {}) => {
    if (["delete", "archive", "unpublish"].includes(action) && !window.confirm(`${action} ${app.name}?`)) return;
    try { await api.post(`/admin/appgen/apps/${id}/action`, { action, ...extra }); toast.success("Done"); load(); onChanged(); } catch (e) { err(e); }
  };
  const publish = async () => {
    if (!window.confirm(`Publish ${app.name} v${app.current_version} publicly on JixxeoApps.com?`)) return;
    try { const r = await api.post(`/admin/appgen/apps/${id}/publish`, { featured: window.confirm("Feature it on the homepage?") }); toast.success("Published"); window.open(r.data.url, "_blank"); load(); onChanged(); } catch (e) { err(e); }
  };
  const requestChange = async () => {
    try { await api.post(`/admin/appgen/apps/${id}/change`, { instructions: change }); setChange(""); toast.success("Rebuilding with your changes"); load(); } catch (e) { err(e); }
  };
  return (
    <div className="glass rounded-2xl p-4 space-y-4" data-testid="appgen-detail">
      <div className="flex flex-wrap items-center gap-3">
        {app.icon_url && <img src={app.icon_url} alt="" className="w-12 h-12 rounded-xl" />}
        <div className="flex-1"><div className="font-heading font-black text-xl">{app.name}</div>
          <div className="mono text-[10px] text-white/50">/g/{app.slug} · status <b data-testid="appgen-status">{app.status}</b> · current v{app.current_version || "–"} · published v{app.published_version || "–"} · ~{app.tokens_total} tokens · est ${Number(app.cost_usd_total || 0).toFixed(3)}</div></div>
      </div>
      {latest && (
        <div className="bg-black/50 rounded-xl p-3 max-h-48 overflow-y-auto mono text-[11px] space-y-0.5" data-testid="appgen-logs">
          <div className="text-white/40">Build v{latest.version} · {latest.status}</div>
          {latest.logs.map((l, i) => <div key={i} className={l.level === "error" ? "text-[#FF003C]" : l.level === "warn" ? "text-[#FFE300]" : "text-white/70"}>{l.at.slice(11, 19)} {l.msg}</div>)}
        </div>
      )}
      {latest?.status === "building" && <BuildProgress v={latest} />}
      {latest?.status === "failed" && <div className="text-xs text-[#FF003C]" data-testid="appgen-failed-hint">This build failed (often a temporary AI service hiccup). Tap Regenerate to try again — the builder now retries automatically with backup models.</div>}
      <div className="flex flex-wrap gap-2">
        <button disabled={!cur} onClick={() => setShowPrev((v) => !v)} data-testid="appgen-preview-btn" className={`${btn} border-white/20`}><Eye className="w-3 h-3" /> {showPrev ? "Hide" : "Live"} preview</button>
        <button disabled={!cur || cur.preview_ok} onClick={() => act("preview_ok", { version: app.current_version })} data-testid="appgen-approve-btn" className={`${btn} border-[#39FF14]/50 text-[#39FF14]`}>{cur?.preview_ok ? "Preview approved" : "Approve preview"}</button>
        <button disabled={!cur?.preview_ok} onClick={publish} data-testid="appgen-publish-btn" className={`${btn} border-[#00F0FF] text-[#00F0FF]`}><Rocket className="w-3 h-3" /> Publish</button>
        <button disabled={app.status === "building"} onClick={() => api.post(`/admin/appgen/apps/${id}/regenerate`).then(load).catch(err)} className={`${btn} border-white/20`}><RefreshCw className="w-3 h-3" /> Regenerate</button>
        {app.status === "published" && <button onClick={() => act("unpublish")} className={`${btn} border-white/20`}><EyeOff className="w-3 h-3" /> Unpublish</button>}
        <button onClick={() => act("duplicate")} className={`${btn} border-white/20`}><Copy className="w-3 h-3" /> Duplicate</button>
        <button onClick={() => act("archive")} className={`${btn} border-white/20`}><Archive className="w-3 h-3" /> Archive</button>
        <button onClick={() => act("delete")} className={`${btn} border-[#FF003C]/40 text-[#FF003C]`}><Trash2 className="w-3 h-3" /> Delete</button>
      </div>
      {showPrev && cur && <><Preview app={app} version={app.current_version} onLog={onLog} />{runtime.length > 0 && <div className="mono text-[10px] text-[#FF003C]" data-testid="appgen-runtime-errors">Runtime errors: {runtime.join(" | ")}</div>}</>}
      <div className="flex gap-2">
        <input value={change} onChange={(e) => setChange(e.target.value)} placeholder="Describe changes (e.g. add a dark mode toggle and a leaderboard)" data-testid="appgen-change-input" className={inp} />
        <button disabled={change.length < 5 || app.status === "building" || !cur} onClick={requestChange} data-testid="appgen-change-btn" className={`${btn} border-[#00F0FF] text-[#00F0FF] shrink-0`}>Update with AI</button>
      </div>
      <div>
        <div className="mono text-[10px] uppercase text-white/40 mb-1">Version history</div>
        {versions.map((v) => (
          <div key={v.id} className="flex flex-wrap items-center gap-3 text-xs py-1 border-b border-white/5" data-testid={`appgen-version-${v.version}`}>
            <b>v{v.version}</b><span className="flex-1 truncate text-white/60">{v.note}</span><span className={v.status === "built" ? "text-[#39FF14]" : v.status === "failed" ? "text-[#FF003C]" : "text-[#00F0FF]"}>{v.status}</span>
            {v.errors?.length > 0 && <span className="text-[#FF003C] truncate max-w-xs">{v.errors[0]}</span>}
            {v.status === "built" && v.version !== app.current_version && <button onClick={() => act("rollback", { version: v.version })} className={`${btn} border-white/15`}><Undo2 className="w-3 h-3" /> Use this</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AppGeneratorTab() {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/appgen/apps").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4" data-testid="admin-appgen-tab">
      <div>
        <h2 className="font-heading font-black text-2xl">Jixxeo AI App Generator</h2>
        <p className="text-xs text-white/50 max-w-2xl">Claude writes a complete single-page web app. It's hosted in an isolated sandbox at /g/&lt;slug&gt; and talks to a Jixxeo data backend through the secure Jixxeo SDK. Members' existing Jixxeo sign-in carries over automatically, and apps never see passwords, cookies or API keys. Nothing goes public until you approve the preview and click Publish. {d && `Builds in last 24h: ${d.limits.builds_last_24h}/${d.limits.daily_builds}.`}</p>
      </div>
      <NewApp onCreated={(id) => { setSel(id); load(); }} />
      {d && d.apps.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {d.apps.map((a) => (
            <button key={a.id} onClick={() => setSel(a.id)} data-testid={`appgen-app-${a.slug}`}
              className={`${btn} ${sel === a.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-white/15 text-white/70"}`}>{a.name} · {a.status} · {a.usage_launches} launches</button>
          ))}
        </div>
      )}
      {sel && <Detail key={sel} id={sel} onChanged={load} />}
    </div>
  );
}
